import React from 'react'
import styled, { keyframes } from 'styled-components'

const spin = keyframes`
  to { transform: rotate(360deg); }
`
const spinBack = keyframes`
  to { transform: rotate(-360deg); }
`
const ring = keyframes`
  0% { transform: scale(.25); opacity: .55; }
  100% { transform: scale(2.8); opacity: 0; }
`
const blink = keyframes`
  0%, 100% { opacity: .85; }
  50% { opacity: .18; }
`
const sweep = keyframes`
  0% { transform: translateX(-40px); opacity: 0; }
  20% { opacity: .5; }
  80% { opacity: .5; }
  100% { transform: translateX(300px); opacity: 0; }
`

const Illustration = styled.svg`
  display: block;
  max-width: 420px;
  overflow: visible;

  .gearBig {
    transform-origin: 0 0;
    animation: ${spin} 26s linear infinite;
  }
  .gearSmall {
    transform-origin: 0 0;
    animation: ${spinBack} 14s linear infinite;
  }
  .ring1,
  .ring2 {
    transform-origin: 0 0;
    animation: ${ring} 3.4s ease-out infinite;
  }
  .ring2 {
    animation-delay: 1.7s;
  }
  .blink {
    animation: ${blink} 3.6s ease-in-out infinite;
  }
  .blink2 {
    animation: ${blink} 4.4s ease-in-out infinite;
    animation-delay: 1.2s;
  }
  .sweep {
    animation: ${sweep} 6s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      animation: none !important;
    }
  }
`

const GEAR_BIG_TEETH = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330]
const GEAR_SMALL_TEETH = [0, 45, 90, 135, 180, 225, 270, 315]

const LIT_WINDOWS = [
  { x: 70, y: 176, className: 'blink' },
  { x: 56, y: 208 },
  { x: 112, y: 144 },
  { x: 140, y: 192, className: 'blink2' },
  { x: 182, y: 80 },
  { x: 210, y: 128, className: 'blink' },
  { x: 196, y: 176 },
  { x: 252, y: 144 },
  { x: 238, y: 192, className: 'blink2' },
]

const MaintenanceIllustration = () => (
  <Illustration
    viewBox="0 0 420 300"
    width="100%"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <linearGradient id="towerFill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#2E6A8C" />
        <stop offset="100%" stopColor="#16405A" />
      </linearGradient>
      <linearGradient id="sweepFill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#8FD49B" stopOpacity="0" />
        <stop offset="50%" stopColor="#8FD49B" stopOpacity=".9" />
        <stop offset="100%" stopColor="#8FD49B" stopOpacity="0" />
      </linearGradient>
      <pattern
        id="windowGrid"
        width="14"
        height="16"
        patternUnits="userSpaceOnUse"
      >
        <rect
          x="0"
          y="0"
          width="8"
          height="10"
          rx="1.5"
          fill="#FFFFFF"
          opacity=".13"
        />
      </pattern>
      <radialGradient id="goldGlow">
        <stop offset="0%" stopColor="#EDBD01" stopOpacity=".28" />
        <stop offset="100%" stopColor="#EDBD01" stopOpacity="0" />
      </radialGradient>
    </defs>

    <circle cx="338" cy="104" r="86" fill="url(#goldGlow)" />

    <g transform="translate(338 104)" opacity=".62">
      <g className="gearBig" fill="#EDBD01">
        {GEAR_BIG_TEETH.map(angle => (
          <rect
            key={angle}
            x="-5.5"
            y="-58"
            width="11"
            height="15"
            rx="2.5"
            transform={`rotate(${angle})`}
          />
        ))}
        <circle r="45" fill="none" stroke="#EDBD01" strokeWidth="10" />
        <circle
          r="15"
          fill="none"
          stroke="#EDBD01"
          strokeWidth="7"
          opacity=".7"
        />
        <path
          d="M0 -38 V-18 M32.9 19 L15.6 9 M-32.9 19 L-15.6 9"
          stroke="#EDBD01"
          strokeWidth="5"
          strokeLinecap="round"
          opacity=".45"
          fill="none"
        />
      </g>
    </g>

    <g transform="translate(300 198)" opacity=".7">
      <g className="gearSmall" fill="#8FD49B">
        {GEAR_SMALL_TEETH.map(angle => (
          <rect
            key={angle}
            x="-4"
            y="-29"
            width="8"
            height="10"
            rx="2"
            transform={`rotate(${angle})`}
          />
        ))}
        <circle r="21" fill="none" stroke="#8FD49B" strokeWidth="7" />
        <circle
          r="6"
          fill="none"
          stroke="#8FD49B"
          strokeWidth="4"
          opacity=".7"
        />
      </g>
    </g>

    <ellipse cx="176" cy="256" rx="168" ry="13" fill="#FFFFFF" opacity=".05" />

    <g>
      <rect
        x="50"
        y="164"
        width="54"
        height="88"
        rx="3"
        fill="url(#towerFill)"
        stroke="#FFFFFF"
        strokeOpacity=".12"
      />
      <rect
        x="106"
        y="100"
        width="54"
        height="152"
        rx="3"
        fill="url(#towerFill)"
        stroke="#FFFFFF"
        strokeOpacity=".12"
      />
      <rect
        x="162"
        y="52"
        width="68"
        height="200"
        rx="3"
        fill="url(#towerFill)"
        stroke="#FFFFFF"
        strokeOpacity=".12"
      />
      <rect
        x="232"
        y="132"
        width="40"
        height="120"
        rx="3"
        fill="url(#towerFill)"
        stroke="#FFFFFF"
        strokeOpacity=".12"
      />

      <rect x="56" y="176" width="42" height="64" fill="url(#windowGrid)" />
      <rect x="112" y="112" width="42" height="128" fill="url(#windowGrid)" />
      <rect x="168" y="64" width="56" height="176" fill="url(#windowGrid)" />
      <rect x="238" y="144" width="28" height="96" fill="url(#windowGrid)" />

      <g fill="#EDBD01">
        {LIT_WINDOWS.map(({ x, y, className }) => (
          <rect
            key={`${x}-${y}`}
            className={className}
            x={x}
            y={y}
            width="8"
            height="10"
            rx="1.5"
            opacity=".85"
          />
        ))}
      </g>

      <path
        d="M196 52 V30"
        stroke="#FFFFFF"
        strokeOpacity=".35"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <g transform="translate(196 28)">
        <circle
          className="ring1"
          r="10"
          fill="none"
          stroke="#8FD49B"
          strokeWidth="2"
        />
        <circle
          className="ring2"
          r="10"
          fill="none"
          stroke="#8FD49B"
          strokeWidth="2"
        />
        <circle r="3.5" fill="#EDBD01" />
      </g>
    </g>

    <rect
      className="sweep"
      x="46"
      y="40"
      width="10"
      height="214"
      fill="url(#sweepFill)"
    />

    <path
      d="M14 252 H406"
      stroke="#FFFFFF"
      strokeOpacity=".16"
      strokeWidth="1.5"
      strokeDasharray="6 9"
    />
  </Illustration>
)

export default MaintenanceIllustration
